/**
 * The surfing progression/achievement taxonomy for "My Swell Breaks" V1.
 *
 * This is a separate, purpose-built 5-level vocabulary (Absolute Beginner →
 * Expert) for the achievement system -- deliberately NOT the same type as
 * planner-vocabulary.ts's 4-value SkillLevel ('beginner'|'intermediate'|
 * 'advanced'|'expert'), which is a locked contract with the recommendation
 * RPC (see CLAUDE.md §5/§11). Conflating the two would either break that
 * contract or force the achievement system into a coarser 4-level model than
 * the product spec calls for. If a later pass wants this progression data to
 * feed the planner, that's a deliberate vocabulary-mapping decision to make
 * then, not something to collapse together now.
 *
 * Pure data/types module -- no React, no Supabase. Nothing here is persisted;
 * see useSurfProgression.ts for the real (Supabase-backed) state layer. This
 * file's ALL_ACHIEVEMENTS ids are the canonical taxonomy that layer validates
 * against in both directions -- see its own comments.
 */

export const SURF_LEVELS = [
  'Absolute Beginner',
  'Beginner',
  'Intermediate',
  'Advanced',
  'Expert',
] as const;

export type SurfLevel = (typeof SURF_LEVELS)[number];

export const nextSurfLevel = (level: SurfLevel): SurfLevel | null => {
  const idx = SURF_LEVELS.indexOf(level);
  return idx >= 0 && idx < SURF_LEVELS.length - 1 ? SURF_LEVELS[idx + 1] : null;
};

/**
 * True when `level` sits below the user's current level -- i.e. it's a
 * prerequisite implicitly satisfied by their self-selected starting point
 * (a user who signs up as Intermediate never sees Absolute Beginner or
 * Beginner presented as "0 achievements", since they said they're past
 * that). Derived from SURF_LEVELS order, not a stored fact -- there is no
 * separate "original starting level" record, only the current one, so this
 * recalculates consistently even if the current level later changes.
 */
export const isPrerequisiteLevel = (level: SurfLevel, currentLevel: SurfLevel): boolean =>
  SURF_LEVELS.indexOf(level) < SURF_LEVELS.indexOf(currentLevel);

export type AchievementCategory =
  | 'Safety & Etiquette'
  | 'Paddling'
  | 'Wave Reading'
  | 'Positioning'
  | 'Take-Offs'
  | 'Speed & Line'
  | 'Manoeuvres'
  | 'Conditions & Confidence'
  | 'Ocean Knowledge'
  | 'Barrel Skills'
  | 'Advanced Performance'
  | 'Progressive Manoeuvres';

export interface Achievement {
  id: string;
  name: string;
  level: SurfLevel;
  category: AchievementCategory;
  /** Short, plain-language definition of the skill -- what it actually is. */
  definition: string;
  /** The kind of wave/environment (or dry-land exercise) best suited to practicing this. */
  bestEnvironment: string;
  /** Concrete, ordered cues for working on it. */
  whatToWorkOn: string[];
  /** True only for the five explicitly-optional Expert-tier progressive manoeuvres. */
  optional?: boolean;
}

// ---------------------------------------------------------------------------
// ABSOLUTE BEGINNER
// ---------------------------------------------------------------------------

const absoluteBeginner: Achievement[] = [
  {
    id: 'ab-surf-safety-basics',
    name: 'Surf Safety Basics',
    level: 'Absolute Beginner',
    category: 'Safety & Etiquette',
    definition: 'Understanding the core rules that keep you and everyone around you safe in the water.',
    bestEnvironment: 'Dry land, before your first session -- a briefing, not a wave.',
    whatToWorkOn: [
      'Learn how to fall safely, away from your board',
      'Know how to hold your board so it doesn’t hit you or others',
      'Understand rip currents and how to spot one',
      'Know when conditions are beyond your ability',
    ],
  },
  {
    id: 'ab-etiquette-basics',
    name: 'Surf Etiquette Basics',
    level: 'Absolute Beginner',
    category: 'Safety & Etiquette',
    definition: 'The unwritten rules that keep a crowded line-up friendly rather than stressful.',
    bestEnvironment: 'Any beginner beach break, watching before you paddle out.',
    whatToWorkOn: [
      'Learn who has priority on a wave',
      'Never drop in on someone already riding',
      'Apologise and reset calmly if you get it wrong',
      'Give more experienced surfers room in the take-off zone',
    ],
  },
  {
    id: 'ab-correct-board-position',
    name: 'Correct Board Position',
    level: 'Absolute Beginner',
    category: 'Positioning',
    definition: 'Lying centred on the board, nose slightly up, so it sits flat and stable in the water.',
    bestEnvironment: '1-2ft sandy-bottom beginner beach, flat water between sets.',
    whatToWorkOn: [
      'Find the balance point where the nose doesn’t dig in or lift too high',
      'Keep your body centred along the stringer',
      'Relax your shoulders and keep your head up',
    ],
  },
  {
    id: 'ab-basic-paddle-technique',
    name: 'Basic Paddle Technique',
    level: 'Absolute Beginner',
    category: 'Paddling',
    definition: 'A clean, alternating stroke that moves you forward efficiently.',
    bestEnvironment: '1-2ft sandy-bottom beginner beach, soft whitewater.',
    whatToWorkOn: [
      'Cup your hands and pull water past your hip',
      'Alternate arms in a steady rhythm',
      'Keep your legs together and still',
    ],
  },
  {
    id: 'ab-paddle-fitness',
    name: 'Paddle Fitness',
    level: 'Absolute Beginner',
    category: 'Paddling',
    definition: 'Building the stamina to paddle for a full session without your arms giving out.',
    bestEnvironment: 'Flat water, or dry-land swimming/prone paddling practice.',
    whatToWorkOn: [
      'Paddle continuously for longer stretches each session',
      'Rest by resting on the board, not by stopping to stand',
      'Build general upper-body and core endurance out of the water',
    ],
  },
  {
    id: 'ab-catching-assisted-whitewater',
    name: 'Catching Assisted Whitewater',
    level: 'Absolute Beginner',
    category: 'Take-Offs',
    definition: 'Catching a wave of whitewater with a push or guidance from an instructor or friend.',
    bestEnvironment: '1-2ft soft whitewater, someone experienced there to help time it.',
    whatToWorkOn: [
      'Get into position and let your helper call the moment to paddle',
      'Paddle hard the instant the wave picks you up',
      'Stay low and centred as the board accelerates',
    ],
  },
  {
    id: 'ab-catching-whitewater-independently',
    name: 'Catching Whitewater Independently',
    level: 'Absolute Beginner',
    category: 'Take-Offs',
    definition: 'Timing and catching whitewater on your own, without a push.',
    bestEnvironment: '1-2ft sandy-bottom beginner beach, consistent whitewater.',
    whatToWorkOn: [
      'Turn and paddle before the whitewater reaches you, not after',
      'Judge how many strokes you need to match its speed',
      'Commit fully once you feel the board start to move',
    ],
  },
  {
    id: 'ab-pop-up-technique',
    name: 'Pop-Up Technique',
    level: 'Absolute Beginner',
    category: 'Take-Offs',
    definition: 'The single motion from lying down to a standing surf stance.',
    bestEnvironment: 'Dry land first, then 1-2ft whitewater.',
    whatToWorkOn: [
      'Push up through your hands, not your knees',
      'Bring both feet under you in one motion',
      'Land with knees bent, weight centred, looking ahead',
    ],
  },
  {
    id: 'ab-pop-up-consistency',
    name: 'Pop-Up Consistency',
    level: 'Absolute Beginner',
    category: 'Take-Offs',
    definition: 'Making the pop-up reliable, wave after wave, without thinking it through step by step.',
    bestEnvironment: '1-2ft sandy-bottom beginner beach, plenty of repetitions.',
    whatToWorkOn: [
      'Repeat the same motion every single time',
      'Pop up as soon as you feel the wave take the board, not later',
      'Trust the motion instead of pausing halfway',
    ],
  },
  {
    id: 'ab-stable-surf-stance',
    name: 'Stable Surf Stance',
    level: 'Absolute Beginner',
    category: 'Positioning',
    definition: 'A balanced, athletic stance -- knees bent, weight low, feet roughly shoulder-width.',
    bestEnvironment: '1-2ft whitewater, or a balance board/skateboard on dry land.',
    whatToWorkOn: [
      'Keep knees bent rather than locking them straight',
      'Distribute weight evenly between both feet',
      'Use your arms loosely for balance rather than holding them stiff',
    ],
  },
  {
    id: 'ab-looking-where-going',
    name: 'Looking Where You’re Going',
    level: 'Absolute Beginner',
    category: 'Positioning',
    definition: 'Keeping your eyes and chin up and pointed down the wave, not down at your feet.',
    bestEnvironment: '1-2ft whitewater.',
    whatToWorkOn: [
      'Pick a point on the beach or shoulder to focus on',
      'Consciously lift your chin during the pop-up',
      'Notice how looking down pulls your balance forward and off',
    ],
  },
  {
    id: 'ab-controlled-whitewater-rides',
    name: 'Controlled Whitewater Rides',
    level: 'Absolute Beginner',
    category: 'Positioning',
    definition: 'Staying up and steady for the length of a whitewater ride, not just popping up briefly.',
    bestEnvironment: '1-2ft sandy-bottom beginner beach.',
    whatToWorkOn: [
      'Keep your stance stable through small bumps in the wave',
      'Make small adjustments with your feet rather than your whole body',
      'Ride it all the way into shallow water before dismounting',
    ],
  },
  {
    id: 'ab-safe-dismount',
    name: 'Safe Dismount',
    level: 'Absolute Beginner',
    category: 'Safety & Etiquette',
    definition: 'Ending a ride and clearing the area without endangering yourself or anyone behind you.',
    bestEnvironment: '1-2ft whitewater, shallow sandy bottom.',
    whatToWorkOn: [
      'Step off to the side rather than diving forward',
      'Keep hold of your board or leash as you fall',
      'Check behind you before stopping in the impact zone',
    ],
  },
];

// ---------------------------------------------------------------------------
// BEGINNER
// ---------------------------------------------------------------------------

const beginner: Achievement[] = [
  {
    id: 'bg-reading-sets-lulls',
    name: 'Reading Sets and Lulls',
    level: 'Beginner',
    category: 'Wave Reading',
    definition: 'Recognising the rhythm of waves arriving in groups (sets) with calmer gaps (lulls) between them.',
    bestEnvironment: '1-3ft mellow beach break, watching from the beach or line-up.',
    whatToWorkOn: [
      'Sit and watch a full set-and-lull cycle before paddling out',
      'Count roughly how many waves per set at this spot today',
      'Use lulls to paddle out or reposition',
    ],
  },
  {
    id: 'bg-identifying-peak',
    name: 'Identifying the Peak',
    level: 'Beginner',
    category: 'Wave Reading',
    definition: 'Spotting where a wave will break first and steepest -- the best take-off spot.',
    bestEnvironment: '1-3ft mellow peeling beach or point break.',
    whatToWorkOn: [
      'Watch a few waves break before choosing your spot',
      'Look for the section that stands up and pitches first',
      'Reposition between waves rather than staying put all session',
    ],
  },
  {
    id: 'bg-choosing-suitable-waves',
    name: 'Choosing Suitable Waves',
    level: 'Beginner',
    category: 'Wave Reading',
    definition: 'Learning to let unsuitable waves pass and paddle only for ones you can realistically catch and ride.',
    bestEnvironment: '1-3ft mellow beach or point break.',
    whatToWorkOn: [
      'Watch how a wave is shaping up before committing',
      'Let closeouts and overly steep waves go by',
      'Be comfortable saying no to a wave',
    ],
  },
  {
    id: 'bg-independent-paddle-out',
    name: 'Independent Paddle-Out',
    level: 'Beginner',
    category: 'Paddling',
    definition: 'Getting from the beach to the line-up on your own, choosing an efficient path through the surf.',
    bestEnvironment: '1-3ft mellow beach break with an easy paddle.',
    whatToWorkOn: [
      'Look for a channel or gap with less whitewater',
      'Time your paddle-out for a lull',
      'Keep paddling through small whitewater rather than stopping',
    ],
  },
  {
    id: 'bg-whitewater-board-control',
    name: 'Whitewater Board Control',
    level: 'Beginner',
    category: 'Paddling',
    definition: 'Keeping your board under control while paddling through breaking whitewater.',
    bestEnvironment: '1-3ft beach break with regular whitewater to push through.',
    whatToWorkOn: [
      'Push the nose down and through smaller whitewater',
      'Hold the rails firmly as whitewater hits',
      'Keep paddling momentum rather than freezing up',
    ],
  },
  {
    id: 'bg-turtle-roll',
    name: 'Turtle Roll',
    level: 'Beginner',
    category: 'Paddling',
    definition: 'Flipping the board upside-down and holding on to pass under larger whitewater.',
    bestEnvironment: '2-3ft beach break, or flat water to drill the motion first.',
    whatToWorkOn: [
      'Grab the rails and roll the board over as whitewater arrives',
      'Pull your body close to the board underneath',
      'Roll back upright and resume paddling immediately after',
    ],
  },
  {
    id: 'bg-basic-duck-dive',
    name: 'Basic Duck Dive',
    level: 'Beginner',
    category: 'Paddling',
    definition: 'Pushing the board underwater, nose-first, to pass beneath smaller breaking waves.',
    bestEnvironment: '2-3ft beach break with a shortboard-style board.',
    whatToWorkOn: [
      'Push down on the nose with your hands as the wave arrives',
      'Use a knee to push the tail under',
      'Let the wave pass overhead before resurfacing and paddling on',
    ],
  },
  {
    id: 'bg-basic-lineup-positioning',
    name: 'Basic Line-Up Positioning',
    level: 'Beginner',
    category: 'Positioning',
    definition: 'Sitting in a sensible spot relative to the peak and other surfers, not drifting into the way.',
    bestEnvironment: '1-3ft mellow beach or point break.',
    whatToWorkOn: [
      'Use a fixed landmark on the beach to hold your position',
      'Notice how currents drift you and reposition regularly',
      'Sit slightly wide of the peak until you’re confident',
    ],
  },
  {
    id: 'bg-applying-priority-rules',
    name: 'Applying Priority Rules',
    level: 'Beginner',
    category: 'Safety & Etiquette',
    definition: 'Putting right-of-way rules into practice in real time, not just knowing them in theory.',
    bestEnvironment: 'Any uncrowded 1-3ft beach or point break.',
    whatToWorkOn: [
      'Check who’s already up or closest to the peak before paddling',
      'Pull back cleanly if someone else has priority',
      'Communicate with a look or a call, not a scramble',
    ],
  },
  {
    id: 'bg-first-green-wave',
    name: 'First Green Wave',
    level: 'Beginner',
    category: 'Take-Offs',
    definition: 'Catching and riding an unbroken (green) wave before it turns to whitewater, for the first time.',
    bestEnvironment: '1-2ft mellow, forgiving beach or point break.',
    whatToWorkOn: [
      'Paddle for a wave slightly before it’s about to break',
      'Pop up as the board starts to accelerate down the face',
      'Keep weight centred as you drop in',
    ],
  },
  {
    id: 'bg-catching-green-waves-independently',
    name: 'Catching Green Waves Independently',
    level: 'Beginner',
    category: 'Take-Offs',
    definition: 'Reliably catching unbroken waves on your own timing, not just occasionally.',
    bestEnvironment: '1-3ft mellow peeling beach or point break.',
    whatToWorkOn: [
      'Judge the take-off window a little earlier each time',
      'Match your paddle speed to the wave’s speed',
      'Commit to the drop rather than pulling back at the last second',
    ],
  },
  {
    id: 'bg-angled-takeoff',
    name: 'Angled Take-Off',
    level: 'Beginner',
    category: 'Take-Offs',
    definition: 'Taking off at an angle along the wave face rather than straight toward the beach.',
    bestEnvironment: '1-3ft mellow peeling beach or point break with a clear shoulder.',
    whatToWorkOn: [
      'Point the nose slightly down the line as you pop up',
      'Look toward the open shoulder, not straight at the beach',
      'Let the wave’s push carry you along the face',
    ],
  },
  {
    id: 'bg-riding-left',
    name: 'Riding Left',
    level: 'Beginner',
    category: 'Take-Offs',
    definition: 'Riding a wave breaking to your left (as you face the beach) with basic control.',
    bestEnvironment: '1-3ft mellow beach or point break peeling left.',
    whatToWorkOn: [
      'Practice this direction deliberately, not just when it happens to line up',
      'Keep your front hand pointed the way you’re going',
      'Notice how it feels different from your natural/stronger side',
    ],
  },
  {
    id: 'bg-riding-right',
    name: 'Riding Right',
    level: 'Beginner',
    category: 'Take-Offs',
    definition: 'Riding a wave breaking to your right (as you face the beach) with basic control.',
    bestEnvironment: '1-3ft mellow beach or point break peeling right.',
    whatToWorkOn: [
      'Practice this direction deliberately, not just when it happens to line up',
      'Keep your front hand pointed the way you’re going',
      'Aim to feel equally comfortable on both sides',
    ],
  },
  {
    id: 'bg-basic-trim',
    name: 'Basic Trim',
    level: 'Beginner',
    category: 'Speed & Line',
    definition: 'Riding along the open face of the wave in a stable line, rather than straight to shore.',
    bestEnvironment: '1-3ft mellow peeling beach or point break.',
    whatToWorkOn: [
      'Angle your board along the wave rather than pointing at the beach',
      'Keep weight centred and steady through the ride',
      'Make small foot adjustments rather than big ones',
    ],
  },
  {
    id: 'bg-controlled-kickout',
    name: 'Controlled Kick-Out',
    level: 'Beginner',
    category: 'Positioning',
    definition: 'Ending a ride deliberately by turning up and over the back of the wave, rather than falling off.',
    bestEnvironment: '1-3ft mellow beach or point break.',
    whatToWorkOn: [
      'Shift weight to your back foot and turn the board uphill',
      'Ride up and over the shoulder rather than straight through it',
      'Keep control of the board on the way out',
    ],
  },
  {
    id: 'bg-independent-beginner-session',
    name: 'Independent Beginner Session',
    level: 'Beginner',
    category: 'Conditions & Confidence',
    definition: 'Surfing a full session -- paddle out, catch waves, come in -- entirely on your own, no instructor.',
    bestEnvironment: '1-3ft mellow beach break you’ve surfed before.',
    whatToWorkOn: [
      'Choose a familiar, forgiving spot for the first solo session',
      'Check conditions and your own readiness beforehand',
      'Set a time limit and stick to it rather than overdoing it',
    ],
  },
];

// ---------------------------------------------------------------------------
// INTERMEDIATE -- the largest section, per the progression philosophy.
// ---------------------------------------------------------------------------

const intermediate: Achievement[] = [
  // Wave Reading / Positioning
  {
    id: 'im-consistent-wave-selection',
    name: 'Consistent Wave Selection',
    level: 'Intermediate',
    category: 'Wave Reading',
    definition: 'Reliably picking waves worth riding, session after session, without wasting energy on the wrong ones.',
    bestEnvironment: '2-4ft clean, long peeling wall with a predictable take-off.',
    whatToWorkOn: [
      'Compare each wave to the last few before paddling',
      'Prioritise shape and shoulder length over size alone',
      'Trust your read and commit early once you’ve chosen',
    ],
  },
  {
    id: 'im-position-at-peak',
    name: 'Position at the Peak',
    level: 'Intermediate',
    category: 'Wave Reading',
    definition: 'Consistently sitting exactly where a wave will first break, not just near it.',
    bestEnvironment: '2-4ft clean point break or reliable beach break peak.',
    whatToWorkOn: [
      'Use two fixed landmarks to triangulate your spot',
      'Adjust between every wave, not just every set',
      'Notice how a few metres of position changes your options',
    ],
  },
  {
    id: 'im-recognise-closeouts',
    name: 'Recognise Closeouts',
    level: 'Intermediate',
    category: 'Wave Reading',
    definition: 'Spotting a wave that will break all at once, with no rideable shoulder, before paddling for it.',
    bestEnvironment: 'A beach break with a mix of peaks and closeouts.',
    whatToWorkOn: [
      'Watch how the wave stands up along its whole length',
      'Look for a section that holds up longer than the rest',
      'Let closeouts pass without frustration -- it’s a read, not a failure',
    ],
  },
  {
    id: 'im-read-shoulder',
    name: 'Read the Shoulder',
    level: 'Intermediate',
    category: 'Wave Reading',
    definition: 'Judging how much open, rideable face a wave will offer before it closes out.',
    bestEnvironment: '2-4ft point break or long peeling beach break.',
    whatToWorkOn: [
      'Watch the wave ahead of your take-off spot before committing',
      'Estimate how far you’ll be able to ride before it shuts down',
      'Plan your line based on that read, not just the drop',
    ],
  },
  {
    id: 'im-anticipate-sections',
    name: 'Anticipate Sections',
    level: 'Intermediate',
    category: 'Wave Reading',
    definition: 'Seeing a fast or closing section coming, in time to adjust your line before you reach it.',
    bestEnvironment: '2-4ft point break with distinct sections.',
    whatToWorkOn: [
      'Look ahead down the line, not just at your board',
      'Identify sections while still riding the one before them',
      'Adjust speed or line early rather than reacting late',
    ],
  },
  {
    id: 'im-adjust-position-between-sets',
    name: 'Adjust Position Between Sets',
    level: 'Intermediate',
    category: 'Positioning',
    definition: 'Actively repositioning during lulls based on what the last set told you.',
    bestEnvironment: '2-4ft beach or point break with shifting peaks.',
    whatToWorkOn: [
      'Review where the best waves of the last set actually broke',
      'Move during the lull, not while a set is arriving',
      'Keep checking rather than settling into one spot all session',
    ],
  },
  {
    id: 'im-confident-lineup-navigation',
    name: 'Confident Line-Up Navigation',
    level: 'Intermediate',
    category: 'Positioning',
    definition: 'Moving through a busy line-up efficiently and predictably, without disrupting others.',
    bestEnvironment: 'A moderately busy 2-4ft beach or point break.',
    whatToWorkOn: [
      'Paddle around the impact zone, not through it, where possible',
      'Signal your intentions clearly to other surfers',
      'Stay aware of who’s paddling for what as you move',
    ],
  },
  // Paddling / Take-Offs
  {
    id: 'im-efficient-duck-dive',
    name: 'Efficient Duck Dive',
    level: 'Intermediate',
    category: 'Paddling',
    definition: 'A duck dive that costs minimal speed and energy, letting you keep paddling straight after.',
    bestEnvironment: '3-5ft beach break with regular whitewater sets to push through.',
    whatToWorkOn: [
      'Time the push so you’re at maximum depth as the wave passes',
      'Keep the board angled to resurface with forward momentum',
      'Resume paddling rhythm immediately, without a pause',
    ],
  },
  {
    id: 'im-paddle-endurance',
    name: 'Paddle Endurance',
    level: 'Intermediate',
    category: 'Paddling',
    definition: 'Sustaining strong paddle power through a full session, including repeated duck dives.',
    bestEnvironment: 'Any session 60+ minutes long, or dedicated paddle-fitness training.',
    whatToWorkOn: [
      'Build a regular paddling or swimming fitness routine',
      'Practice pacing effort rather than sprinting every wave',
      'Notice and manage fatigue before it affects your judgment',
    ],
  },
  {
    id: 'im-fast-takeoff',
    name: 'Fast Take-Off',
    level: 'Intermediate',
    category: 'Take-Offs',
    definition: 'Getting from paddling to standing and in control in the shortest possible time.',
    bestEnvironment: '2-4ft wave with a punchy, quick-standing take-off.',
    whatToWorkOn: [
      'Reduce the number of motions between paddling and standing',
      'Pop up the instant you feel the board catch the wave',
      'Drill the pop-up until it’s automatic under pressure',
    ],
  },
  {
    id: 'im-angled-takeoff-int',
    name: 'Angled Take-Off (Intermediate)',
    level: 'Intermediate',
    category: 'Take-Offs',
    definition: 'Taking off already angled down the line, at speed, rather than adjusting after standing.',
    bestEnvironment: '2-4ft point break or long peeling beach break.',
    whatToWorkOn: [
      'Set your intended direction with your last paddle strokes',
      'Land the pop-up already facing down the line',
      'Avoid a straight drop followed by a late turn',
    ],
  },
  {
    id: 'im-frontside-takeoff',
    name: 'Frontside Take-Off',
    level: 'Intermediate',
    category: 'Take-Offs',
    definition: 'Taking off on a wave breaking toward your front (facing the wave as you ride).',
    bestEnvironment: '2-4ft clean wave breaking to your frontside.',
    whatToWorkOn: [
      'Keep your chest and eyes facing down the line',
      'Use your front arm to help initiate direction',
      'Build confidence here before pushing backside progression',
    ],
  },
  {
    id: 'im-backside-takeoff',
    name: 'Backside Take-Off',
    level: 'Intermediate',
    category: 'Take-Offs',
    definition: 'Taking off on a wave breaking behind you, a genuinely different skill from frontside.',
    bestEnvironment: '2-3ft clean, forgiving wave breaking to your backside.',
    whatToWorkOn: [
      'Look over your back shoulder to spot your line',
      'Get comfortable with reduced visibility of the wave face',
      'Practice this side deliberately, not just when it happens to line up',
    ],
  },
  {
    id: 'im-taking-off-deeper',
    name: 'Taking Off Deeper',
    level: 'Intermediate',
    category: 'Take-Offs',
    definition: 'Committing to the take-off closer to the critical, steepest part of the wave.',
    bestEnvironment: '2-4ft wave with a well-defined, powerful peak.',
    whatToWorkOn: [
      'Push your take-off spot a little later each session',
      'Trust the pop-up rather than pulling back when it feels steep',
      'Build this gradually -- a few feet at a time',
    ],
  },
  // Speed & Line
  {
    id: 'im-efficient-trim',
    name: 'Efficient Trim',
    level: 'Intermediate',
    category: 'Speed & Line',
    definition: 'Holding a fast, stable line across the wave face with minimal wasted movement.',
    bestEnvironment: '2-4ft clean, long peeling wall.',
    whatToWorkOn: [
      'Find the sweet spot on your board where speed feels effortless',
      'Keep your board flat and your line smooth',
      'Avoid unnecessary weight shifts that slow you down',
    ],
  },
  {
    id: 'im-hold-high-line',
    name: 'Hold a High Line',
    level: 'Intermediate',
    category: 'Speed & Line',
    definition: 'Riding near the top/critical part of the wave rather than drifting into the flat, slower section.',
    bestEnvironment: '2-4ft point break with a well-defined pocket.',
    whatToWorkOn: [
      'Keep your board angled up toward the pocket, not straight down the line',
      'Notice how staying high keeps your speed options open',
      'Drop down deliberately for a turn, then climb back up',
    ],
  },
  {
    id: 'im-generate-speed',
    name: 'Generate Speed',
    level: 'Intermediate',
    category: 'Speed & Line',
    definition: 'Actively creating speed through pumping/trimming, not just relying on the wave’s own power.',
    bestEnvironment: '2-4ft wave with a soft, less powerful shoulder.',
    whatToWorkOn: [
      'Compress down then extend up through the knees, timed with the wave',
      'Angle the board slightly to convert vertical movement into speed',
      'Practice on weaker sections where speed doesn’t come for free',
    ],
  },
  {
    id: 'im-compression-extension',
    name: 'Compression and Extension',
    level: 'Intermediate',
    category: 'Speed & Line',
    definition: 'Using your legs -- crouching then standing tall -- as the engine behind speed and turns.',
    bestEnvironment: '2-4ft clean, long peeling wall.',
    whatToWorkOn: [
      'Drop low through your knees on the down-line sections',
      'Extend upward through a turn to add power',
      'Time the rhythm with the wave, not on a fixed count',
    ],
  },
  {
    id: 'im-make-faster-sections',
    name: 'Make Faster Sections',
    level: 'Intermediate',
    category: 'Speed & Line',
    definition: 'Successfully riding through a section of the wave that’s moving faster than you are, without getting caught behind it.',
    bestEnvironment: '2-4ft point break with a genuine fast section.',
    whatToWorkOn: [
      'Build speed well before you reach the fast section',
      'Take the highest, straightest line through it',
      'Accept you’ll sometimes get caught -- it’s part of learning the read',
    ],
  },
  {
    id: 'im-control-speed',
    name: 'Control Speed',
    level: 'Intermediate',
    category: 'Speed & Line',
    definition: 'Slowing down or holding back deliberately, not just going as fast as possible at all times.',
    bestEnvironment: '2-4ft wave with a mix of fast and slow sections.',
    whatToWorkOn: [
      'Use a wider turn or flatter line to shed speed',
      'Practice waiting for a section rather than always racing ahead',
      'Notice when slowing down actually keeps you in the pocket longer',
    ],
  },
  // Manoeuvres
  {
    id: 'im-functional-bottom-turn',
    name: 'Functional Bottom Turn',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'A basic, controlled turn at the base of the wave that redirects you back up the face.',
    bestEnvironment: '2-4ft clean, long peeling wall with a predictable take-off and enough power for manoeuvres without excessive consequence.',
    whatToWorkOn: [
      'Drop down the face with your weight centred',
      'Engage your rail smoothly as you turn',
      'Redirect toward the open shoulder, not straight up',
    ],
  },
  {
    id: 'im-frontside-bottom-turn',
    name: 'Frontside Bottom Turn',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'A bottom turn performed facing the wave.',
    bestEnvironment: '2-4ft clean, long peeling wall.',
    whatToWorkOn: [
      'Look up the face toward where you want to go',
      'Drive through your front foot for power',
      'Keep the turn smooth rather than abrupt',
    ],
  },
  {
    id: 'im-backside-bottom-turn',
    name: 'Backside Bottom Turn',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'A bottom turn performed with your back to the wave -- a distinct skill from frontside.',
    bestEnvironment: '2-3ft clean, forgiving wave breaking to your backside.',
    whatToWorkOn: [
      'Look over your shoulder to spot the wave face',
      'Use your back arm and hip to initiate rotation',
      'Trust the turn even with reduced visibility',
    ],
  },
  {
    id: 'im-basic-top-turn',
    name: 'Basic Top Turn',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'Redirecting back down the face after riding up near the top of the wave.',
    bestEnvironment: '2-4ft clean, long peeling wall.',
    whatToWorkOn: [
      'Ride your bottom turn up into the pocket',
      'Turn your shoulders and hips back toward the beach',
      'Redirect down the face with controlled speed',
    ],
  },
  {
    id: 'im-basic-cutback',
    name: 'Basic Cutback',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'Redirect back toward the breaking part of the wave before reconnecting with the open face.',
    bestEnvironment: '2-4ft long peeling point break with a softer shoulder and clearly defined pocket.',
    whatToWorkOn: [
      'Look back toward the pocket',
      'Compress',
      'Engage rail',
      'Rotate shoulders',
      'Reconnect with the pocket',
      'Redirect down the line',
    ],
  },
  {
    id: 'im-basic-reentry',
    name: 'Basic Re-Entry',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'Hitting the lip of the wave and redirecting back down, off the top.',
    bestEnvironment: '2-4ft wave with a defined, makeable lip section.',
    whatToWorkOn: [
      'Drive up toward the lip with speed',
      'Hit it at an angle rather than straight on',
      'Redirect down the face as you come off',
    ],
  },
  {
    id: 'im-floater',
    name: 'Floater',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'Riding up and over a breaking section of the wave, then coming back down on the other side.',
    bestEnvironment: '2-4ft wave with a soft, closing section to float over.',
    whatToWorkOn: [
      'Approach the breaking section with speed',
      'Flatten your board as you ride up onto the whitewater',
      'Keep weight centred as you descend back to the face',
    ],
  },
  {
    id: 'im-link-two-turns',
    name: 'Link Two Turns',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'Connecting a bottom turn into a top turn (or two turns in sequence) in one flowing ride.',
    bestEnvironment: '2-4ft clean, long peeling wall.',
    whatToWorkOn: [
      'Keep speed through the exit of the first turn',
      'Set up your line for the second turn before finishing the first',
      'Focus on flow between turns, not just the turns themselves',
    ],
  },
  {
    id: 'im-surf-top-to-bottom',
    name: 'Surf Top-to-Bottom',
    level: 'Intermediate',
    category: 'Manoeuvres',
    definition: 'Using the full vertical face of the wave, from the pocket to the base, in one ride.',
    bestEnvironment: '2-4ft clean, long peeling wall with real vertical face.',
    whatToWorkOn: [
      'Climb high into the pocket after your bottom turn',
      'Come back down with control, not just drift down',
      'Repeat the up-and-down rhythm through the ride',
    ],
  },
  // Conditions & Independence
  {
    id: 'im-comfortable-3ft',
    name: 'Comfortable in 3ft Surf',
    level: 'Intermediate',
    category: 'Conditions & Confidence',
    definition: 'Surfing 3ft waves with the same ease and decision-making as smaller surf.',
    bestEnvironment: 'A trusted, familiar 3ft beach or point break.',
    whatToWorkOn: [
      'Build up from smaller days at the same spot',
      'Notice how paddle power and timing need to increase',
      'Stay relaxed rather than tensing up at the extra size',
    ],
  },
  {
    id: 'im-comfortable-4ft',
    name: 'Comfortable in 4ft Surf',
    level: 'Intermediate',
    category: 'Conditions & Confidence',
    definition: 'Surfing 4ft waves confidently, including the extra power and consequence they carry.',
    bestEnvironment: 'A trusted, familiar 4ft beach or point break.',
    whatToWorkOn: [
      'Only push into 4ft once 3ft feels fully comfortable',
      'Respect the extra hold-down time if you fall',
      'Keep your wave selection more conservative at this size',
    ],
  },
  {
    id: 'im-confident-point-break',
    name: 'Confident Point-Break Surfing',
    level: 'Intermediate',
    category: 'Conditions & Confidence',
    definition: 'Surfing a point break with an understanding of the paddle-out, line-up, and long rides it involves.',
    bestEnvironment: '2-4ft point break with a clear channel.',
    whatToWorkOn: [
      'Learn the specific paddle-out route for the spot',
      'Understand how the wave changes along its length',
      'Get comfortable with longer rides and longer paddles back',
    ],
  },
  {
    id: 'im-mellow-reef-break',
    name: 'Mellow Reef-Break Surfing',
    level: 'Intermediate',
    category: 'Conditions & Confidence',
    definition: 'Surfing a forgiving reef break with awareness of the bottom beneath you.',
    bestEnvironment: 'A well-known, mellow reef break at moderate size.',
    whatToWorkOn: [
      'Learn the tide range the reef is surfable and safe at',
      'Understand where the deep channel and shallow sections are',
      'Keep extra distance from the reef until you know it well',
    ],
  },
  {
    id: 'im-understand-tide-effects',
    name: 'Understand Tide Effects',
    level: 'Intermediate',
    category: 'Ocean Knowledge',
    definition: 'Knowing how a rising or falling tide changes a given wave’s shape and power.',
    bestEnvironment: 'Any spot you can watch across a full tide cycle.',
    whatToWorkOn: [
      'Watch the same spot at different tide stages',
      'Ask locals or check a forecast for this spot’s best tide',
      'Notice how take-off spots shift as the tide moves',
    ],
  },
  {
    id: 'im-understand-wind-effects',
    name: 'Understand Wind Effects',
    level: 'Intermediate',
    category: 'Ocean Knowledge',
    definition: 'Recognising how onshore, offshore, and cross winds change wave quality.',
    bestEnvironment: 'Any spot, comparing sessions across different wind directions.',
    whatToWorkOn: [
      'Notice the difference between glassy and choppy conditions',
      'Learn which wind direction is offshore at your local spot',
      'Factor wind into your decision on whether to paddle out',
    ],
  },
  {
    id: 'im-read-surf-forecast',
    name: 'Read a Surf Forecast',
    level: 'Intermediate',
    category: 'Ocean Knowledge',
    definition: 'Using swell size, period, wind, and tide data to judge conditions before you arrive.',
    bestEnvironment: 'Anywhere -- this is a planning skill, practiced before paddling out.',
    whatToWorkOn: [
      'Compare a forecast’s prediction to what you actually find',
      'Learn what swell period means for wave power and shape',
      'Build a habit of checking before every session',
    ],
  },
  {
    id: 'im-choose-appropriate-board',
    name: 'Choose an Appropriate Board',
    level: 'Intermediate',
    category: 'Ocean Knowledge',
    definition: 'Matching your board to the day’s conditions and your own goals for the session.',
    bestEnvironment: 'Any session where you have more than one board option.',
    whatToWorkOn: [
      'Notice how board volume and shape change how a session feels',
      'Try a slightly different board on a small or weak day',
      'Ask what a board is actually suited for before borrowing/buying',
    ],
  },
];

// ---------------------------------------------------------------------------
// ADVANCED
// ---------------------------------------------------------------------------

const advanced: Achievement[] = [
  {
    id: 'ad-powerful-bottom-turn',
    name: 'Powerful Bottom Turn',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'A bottom turn driven with real power and speed, setting up a strong next manoeuvre.',
    bestEnvironment: '4-6ft wave with a well-defined, powerful face.',
    whatToWorkOn: [
      'Drive hard off your back foot through the turn',
      'Use the wave’s own power, not just your own',
      'Exit the turn already accelerating toward your next move',
    ],
  },
  {
    id: 'ad-vertical-reentry',
    name: 'Vertical Re-Entry',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'A re-entry taken fully vertical off the lip, not just a soft redirection.',
    bestEnvironment: 'A wave with a clean, makeable lip and real power.',
    whatToWorkOn: [
      'Approach the lip with more speed than a basic re-entry needs',
      'Drive your board vertically up the face',
      'Land committed, weight forward, ready to keep riding',
    ],
  },
  {
    id: 'ad-roundhouse-cutback',
    name: 'Roundhouse Cutback',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'A full, sweeping cutback with a rebound off the whitewater, not just a redirection back to the pocket.',
    bestEnvironment: 'A long, open-face wave with a soft shoulder to cut back into.',
    whatToWorkOn: [
      'Commit to a wider, fuller arc than a basic cutback',
      'Use the whitewater to rebound your direction again',
      'Reconnect with speed, not just direction',
    ],
  },
  {
    id: 'ad-cutback-to-rebound',
    name: 'Cutback to Rebound',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'Linking a cutback directly into a rebound off the broken whitewater in one flowing motion.',
    bestEnvironment: 'A wave with a defined pocket and soft whitewater section behind it.',
    whatToWorkOn: [
      'Keep speed through the cutback itself',
      'Use the whitewater’s energy rather than fighting it',
      'Redirect smoothly back toward the open face',
    ],
  },
  {
    id: 'ad-powerful-backside-turn',
    name: 'Powerful Backside Turn',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'A backside turn performed with real commitment and power, not just a controlled redirection.',
    bestEnvironment: 'A powerful wave breaking to your backside.',
    whatToWorkOn: [
      'Drive through your back foot and hip rotation',
      'Commit your weight fully despite reduced visibility',
      'Build power progressively from smaller backside turns',
    ],
  },
  {
    id: 'ad-advanced-floater',
    name: 'Advanced Floater',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'A floater performed with speed and control over a genuinely critical section.',
    bestEnvironment: 'A wave with a hollow, closing section to float across.',
    whatToWorkOn: [
      'Approach with more speed than a basic floater needs',
      'Stay light and centred as the section closes beneath you',
      'Land with speed to continue the ride, not just survive it',
    ],
  },
  {
    id: 'ad-linking-multiple-manoeuvres',
    name: 'Linking Multiple Manoeuvres',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'Stringing three or more manoeuvres together in a single ride without losing speed or flow.',
    bestEnvironment: 'A long, open-face wave with multiple sections.',
    whatToWorkOn: [
      'Plan your line for the whole wave, not turn by turn',
      'Preserve speed as the top priority between manoeuvres',
      'Practice on familiar waves before trying it somewhere new',
    ],
  },
  {
    id: 'ad-surfing-in-pocket',
    name: 'Surfing in the Pocket',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'Consistently riding in the most powerful part of the wave, close to where it’s breaking.',
    bestEnvironment: 'A wave with a clearly defined, powerful pocket.',
    whatToWorkOn: [
      'Push your line higher and closer to the breaking section',
      'Accept the extra risk that comes with the extra power',
      'Use speed to stay ahead of the pocket rather than get caught',
    ],
  },
  {
    id: 'ad-top-to-bottom-surfing',
    name: 'Top-to-Bottom Surfing',
    level: 'Advanced',
    category: 'Manoeuvres',
    definition: 'Using the entire vertical face on nearly every turn, not just occasionally.',
    bestEnvironment: 'A wave with real vertical face and power.',
    whatToWorkOn: [
      'Make full-face use a habit, not a highlight',
      'Keep speed and control through the full vertical range',
      'Review footage or feedback to check you’re using the whole wave',
    ],
  },
  {
    id: 'ad-maintaining-speed-through-turns',
    name: 'Maintaining Speed Through Turns',
    level: 'Advanced',
    category: 'Speed & Line',
    definition: 'Coming out of every turn with as much (or more) speed than you went in with.',
    bestEnvironment: '4-6ft confident, powerful surf.',
    whatToWorkOn: [
      'Focus on the exit of a turn as much as the turn itself',
      'Use compression and extension to add speed, not just redirect',
      'Notice which turns are currently costing you speed and why',
    ],
  },
  {
    id: 'ad-late-takeoff',
    name: 'Late Take-Off',
    level: 'Advanced',
    category: 'Take-Offs',
    definition: 'Catching a wave later than feels comfortable, right as it’s about to break.',
    bestEnvironment: 'A wave with a defined, makeable peak and some consequence for missing it.',
    whatToWorkOn: [
      'Push your take-off timing a fraction later each session',
      'Commit fully once you’ve chosen to go late',
      'Build this progressively -- it’s a confidence skill as much as technical',
    ],
  },
  {
    id: 'ad-steep-takeoff',
    name: 'Steep Take-Off',
    level: 'Advanced',
    category: 'Take-Offs',
    definition: 'Dropping in on a genuinely steep wave face under control.',
    bestEnvironment: 'A wave with a steep, powerful drop.',
    whatToWorkOn: [
      'Keep weight low and centred through the drop',
      'Look ahead, not down the face, as you descend',
      'Build up from moderately steep drops before the steepest ones',
    ],
  },
  {
    id: 'ad-confident-4-6ft',
    name: 'Confident 4-6ft Surfing',
    level: 'Advanced',
    category: 'Conditions & Confidence',
    definition: 'Surfing 4-6ft waves with the same composure and decision-making as smaller surf.',
    bestEnvironment: 'A trusted 4-6ft spot you know well.',
    whatToWorkOn: [
      'Build up from consistent 4ft sessions',
      'Respect the increased hold-down and paddle-out difficulty',
      'Keep your wave selection sharp at this size',
    ],
  },
  {
    id: 'ad-confident-reef-surfing',
    name: 'Confident Reef Surfing',
    level: 'Advanced',
    category: 'Conditions & Confidence',
    definition: 'Surfing a genuine reef break -- not just a mellow one -- with real awareness of the hazard beneath.',
    bestEnvironment: 'A well-known reef break, ideally with local knowledge available first.',
    whatToWorkOn: [
      'Learn the reef’s specific hazards and safe channels',
      'Understand what tide/size combination is safe here',
      'Never surf an unfamiliar reef alone for the first time',
    ],
  },
  {
    id: 'ad-complex-entries-exits',
    name: 'Complex Entries/Exits',
    level: 'Advanced',
    category: 'Conditions & Confidence',
    definition: 'Getting in and out of the water at spots with rocks, currents, or unusual access.',
    bestEnvironment: 'A spot with a non-trivial entry (rocky point, current-affected channel).',
    whatToWorkOn: [
      'Watch how experienced locals time their entry/exit',
      'Identify the safest route before committing',
      'Have a backup plan if your first attempt doesn’t work',
    ],
  },
  {
    id: 'ad-current-management',
    name: 'Current Management',
    level: 'Advanced',
    category: 'Ocean Knowledge',
    definition: 'Using or working with ocean currents deliberately, rather than being pushed around by them.',
    bestEnvironment: 'Any spot with a noticeable current or rip.',
    whatToWorkOn: [
      'Identify the current’s direction and strength before paddling out',
      'Use a rip to assist your paddle-out where safe to do so',
      'Reposition proactively rather than fighting the drift',
    ],
  },
  {
    id: 'ad-crowded-lineup-positioning',
    name: 'Crowded Line-Up Positioning',
    level: 'Advanced',
    category: 'Ocean Knowledge',
    definition: 'Holding a good position and getting waves in a genuinely crowded, competitive line-up.',
    bestEnvironment: 'A popular, busy break at a good time of day.',
    whatToWorkOn: [
      'Earn position patiently rather than paddling straight to the peak',
      'Read the crowd’s rotation and rhythm',
      'Stay respectful under pressure -- this is a social skill too',
    ],
  },
  {
    id: 'ad-adapting-unfamiliar-breaks',
    name: 'Adapting to Unfamiliar Breaks',
    level: 'Advanced',
    category: 'Ocean Knowledge',
    definition: 'Reading and adjusting to a wave you’ve never surfed before, efficiently.',
    bestEnvironment: 'Any new-to-you break, ideally scouted from the beach first.',
    whatToWorkOn: [
      'Watch for 10-15 minutes before paddling out anywhere new',
      'Identify the peak, channel, and hazards from the beach',
      'Start conservative and build confidence within the session',
    ],
  },
  {
    id: 'ad-recognising-barrel-sections',
    name: 'Recognising Barrel Sections',
    level: 'Advanced',
    category: 'Barrel Skills',
    definition: 'Spotting the part of a wave that’s about to throw a hollow, ridable barrel section.',
    bestEnvironment: 'A wave known for barrel sections, at a size that offers a genuine tube.',
    whatToWorkOn: [
      'Watch how the wave pitches and hollows out ahead of you',
      'Learn this specific wave’s barrel tendencies over multiple sessions',
      'Start by simply identifying the section, before riding into it',
    ],
  },
  {
    id: 'ad-pulling-into-barrels',
    name: 'Pulling Into Barrels',
    level: 'Advanced',
    category: 'Barrel Skills',
    definition: 'Committing your line into a barrel section rather than pulling out or going around it.',
    bestEnvironment: 'A forgiving barrel with an easy exit, at manageable size.',
    whatToWorkOn: [
      'Set your line early, before the section throws',
      'Get low and compact as you enter',
      'Commit fully rather than hesitating at the entry',
    ],
  },
  {
    id: 'ad-setting-barrel-line',
    name: 'Setting a Barrel Line',
    level: 'Advanced',
    category: 'Barrel Skills',
    definition: 'Choosing and holding the right line to stay inside a barrel section.',
    bestEnvironment: 'A wave with a defined, somewhat predictable barrel section.',
    whatToWorkOn: [
      'Aim slightly higher on the face than instinct suggests',
      'Adjust your line based on how the barrel is throwing',
      'Stay patient rather than racing straight out',
    ],
  },
  {
    id: 'ad-barrel-speed-control',
    name: 'Barrel Speed Control',
    level: 'Advanced',
    category: 'Barrel Skills',
    definition: 'Adjusting your speed inside the barrel to stay ahead of it closing on you.',
    bestEnvironment: 'A wave with a barrel section that offers a real speed challenge.',
    whatToWorkOn: [
      'Pump for speed if the section is outrunning you',
      'Trim higher and slower if you’re getting ahead of it',
      'Feel for the barrel’s rhythm rather than fixing on one speed',
    ],
  },
  {
    id: 'ad-making-a-barrel',
    name: 'Making a Barrel',
    level: 'Advanced',
    category: 'Barrel Skills',
    definition: 'Successfully riding all the way through a barrel section and out the other side.',
    bestEnvironment: 'A makeable barrel, forgiving enough for a realistic first success.',
    whatToWorkOn: [
      'Combine your line, speed, and commitment from prior sessions',
      'Stay low and trust your speed rather than standing up early',
      'Celebrate the attempts, not just the makes -- this takes reps',
    ],
  },
  {
    id: 'ad-backside-barrel-technique',
    name: 'Backside Barrel Technique',
    level: 'Advanced',
    category: 'Barrel Skills',
    definition: 'Riding a barrel with your back to the wave -- a distinct, harder skill from frontside barrels.',
    bestEnvironment: 'A forgiving backside barrel at manageable size.',
    whatToWorkOn: [
      'Get low with your inside hand trailing on the face',
      'Look back over your shoulder to track the barrel',
      'Build confidence here separately from frontside progress',
    ],
  },
];

// ---------------------------------------------------------------------------
// EXPERT -- a complete surfer, not simply "surfs bigger waves."
// ---------------------------------------------------------------------------

const expert: Achievement[] = [
  {
    id: 'ex-critical-bottom-turn',
    name: 'Critical Bottom Turn',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'A bottom turn executed at full commitment and speed in genuinely consequential surf.',
    bestEnvironment: 'Overhead, powerful surf with real consequence.',
    whatToWorkOn: [
      'Trust technique built at lower stakes to hold up under pressure',
      'Commit fully rather than easing off in bigger conditions',
      'Debrief each session honestly on what held and what didn’t',
    ],
  },
  {
    id: 'ex-vertical-lip-attack',
    name: 'Vertical Lip Attack',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'An aggressive, fully vertical hit on the lip, redirecting with real speed and power.',
    bestEnvironment: 'A powerful wave with a well-defined, hittable lip.',
    whatToWorkOn: [
      'Carry more speed into the lip than feels comfortable',
      'Commit your rail fully through the redirection',
      'Land driving forward, ready for the next move',
    ],
  },
  {
    id: 'ex-advanced-rail-control',
    name: 'Advanced Rail Control',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'Precise control over how much rail is engaged at any moment, across every manoeuvre.',
    bestEnvironment: 'Any high-performance wave -- this is a technique refinement, not condition-specific.',
    whatToWorkOn: [
      'Notice the difference a few degrees of rail angle makes',
      'Practice deliberately varying rail engagement mid-turn',
      'Review footage to spot where control breaks down',
    ],
  },
  {
    id: 'ex-critical-reentry',
    name: 'Critical Re-Entry',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'A re-entry taken on the most powerful, consequential part of a critical section.',
    bestEnvironment: 'A powerful wave with a genuinely critical lip section.',
    whatToWorkOn: [
      'Commit to the steepest, most powerful part of the section',
      'Maintain technique even as consequence increases',
      'Build up through progressively more critical sections',
    ],
  },
  {
    id: 'ex-advanced-turn-combinations',
    name: 'Advanced Turn Combinations',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'Combining different turn types fluidly within a single ride, adapted to what the wave offers.',
    bestEnvironment: 'A wave with varied sections requiring different responses.',
    whatToWorkOn: [
      'Read each section and choose the right turn for it, not a default',
      'Practice transitioning between turn types smoothly',
      'Prioritise wave-appropriate choices over showing range for its own sake',
    ],
  },
  {
    id: 'ex-maintaining-technique-under-pressure',
    name: 'Maintaining Technique Under Pressure',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'Keeping clean technique even in heavy, intimidating, or high-consequence conditions.',
    bestEnvironment: 'Genuinely challenging surf, at or near your current limit.',
    whatToWorkOn: [
      'Notice which parts of your technique break down under pressure first',
      'Work on those specifically at a slightly lower-pressure level',
      'Build confidence gradually rather than forcing it in the deep end',
    ],
  },
  {
    id: 'ex-high-level-backside-surfing',
    name: 'High-Level Backside Surfing',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'Backside surfing at the same standard and power as your frontside surfing.',
    bestEnvironment: 'A powerful wave breaking to your backside.',
    whatToWorkOn: [
      'Dedicate deliberate sessions to backside-only progression',
      'Compare footage of both sides honestly',
      'Close the gap turn by turn rather than expecting it all at once',
    ],
  },
  {
    id: 'ex-adapting-performance-wave-types',
    name: 'Adapting Performance to Different Wave Types',
    level: 'Expert',
    category: 'Advanced Performance',
    definition: 'Adjusting your approach and technique appropriately across beach breaks, points, and reefs.',
    bestEnvironment: 'Deliberately varied -- surf different wave types across a travel season.',
    whatToWorkOn: [
      'Notice what changes about your surfing at each wave type',
      'Identify which wave type is currently your weakest',
      'Seek out that wave type specifically to close the gap',
    ],
  },
  {
    id: 'ex-consistent-barrel-entry',
    name: 'Consistent Barrel Entry',
    level: 'Expert',
    category: 'Barrel Skills',
    definition: 'Reliably getting into barrel sections, not just occasionally.',
    bestEnvironment: 'A wave with regular barrel opportunities.',
    whatToWorkOn: [
      'Refine your entry timing across many attempts',
      'Build a repeatable, trusted entry technique',
      'Track your make/miss rate honestly to see real progress',
    ],
  },
  {
    id: 'ex-deep-barrel-positioning',
    name: 'Deep Barrel Positioning',
    level: 'Expert',
    category: 'Barrel Skills',
    definition: 'Holding a deep, tucked-in position inside the barrel rather than riding its outer edge.',
    bestEnvironment: 'A wave with a defined, hollow barrel section.',
    whatToWorkOn: [
      'Push your line deeper on waves you already know you can make',
      'Get low and compact to fit deeper inside the tube',
      'Accept more misses as the trade-off for going deeper',
    ],
  },
  {
    id: 'ex-advanced-barrel-speed-control',
    name: 'Advanced Barrel Speed Control',
    level: 'Expert',
    category: 'Barrel Skills',
    definition: 'Fine-tuned speed adjustment inside a barrel across changing, unpredictable sections.',
    bestEnvironment: 'A wave with variable, less predictable barrel sections.',
    whatToWorkOn: [
      'React to the barrel’s changing shape in real time',
      'Use small pumps and trim adjustments rather than big corrections',
      'Build a feel for this specific wave across repeated sessions',
    ],
  },
  {
    id: 'ex-backside-barrel-control',
    name: 'Backside Barrel Control',
    level: 'Expert',
    category: 'Barrel Skills',
    definition: 'The same fine speed and line control in a backside barrel as an expert frontside barrel rider has.',
    bestEnvironment: 'A quality backside barrel wave.',
    whatToWorkOn: [
      'Dedicate focused sessions to backside barrels specifically',
      'Refine the low, trailing-hand stance for better control',
      'Compare your backside and frontside barrel-riding honestly',
    ],
  },
  {
    id: 'ex-technical-barrel-exit',
    name: 'Technical Barrel Exit',
    level: 'Expert',
    category: 'Barrel Skills',
    definition: 'Exiting a barrel cleanly and with speed, including from difficult or closing exits.',
    bestEnvironment: 'A wave with a genuinely tricky barrel exit.',
    whatToWorkOn: [
      'Anticipate the exit before you’re fully through the barrel',
      'Keep speed and a low stance right through the exit',
      'Practice standing tall and riding away rather than kicking out early',
    ],
  },
  {
    id: 'ex-multiple-barrel-sections',
    name: 'Multiple Barrel Sections',
    level: 'Expert',
    category: 'Barrel Skills',
    definition: 'Riding through more than one distinct barrel section on the same wave.',
    bestEnvironment: 'A wave known for offering multiple barrel opportunities in one ride.',
    whatToWorkOn: [
      'Exit the first section with enough speed to reach the next',
      'Reset your stance between sections without losing your line',
      'Learn the specific wave’s multi-section rhythm over time',
    ],
  },
  {
    id: 'ex-overhead-powerful-surf',
    name: 'Overhead Powerful Surf',
    level: 'Expert',
    category: 'Conditions & Confidence',
    definition: 'Surfing genuinely overhead, powerful waves with full composure.',
    bestEnvironment: 'A trusted, well-understood overhead spot.',
    whatToWorkOn: [
      'Build up through progressively larger, familiar days',
      'Respect the increased hold-down and paddle demands',
      'Never push into overhead surf alone at an unfamiliar spot',
    ],
  },
  {
    id: 'ex-technical-reef-surfing',
    name: 'Technical Reef Surfing',
    level: 'Expert',
    category: 'Conditions & Confidence',
    definition: 'Surfing genuinely demanding, shallow, or fast reef breaks with precision.',
    bestEnvironment: 'A well-known, technical reef break, ideally with prior local knowledge.',
    whatToWorkOn: [
      'Study the reef’s specific hazards and tide windows in detail',
      'Build up via mellower reefs before the most technical ones',
      'Always have a clear exit plan before paddling out',
    ],
  },
  {
    id: 'ex-critical-takeoffs',
    name: 'Critical Take-Offs',
    level: 'Expert',
    category: 'Take-Offs',
    definition: 'Committing to take-offs at the most consequential, latest, steepest moment a wave offers.',
    bestEnvironment: 'A powerful wave with a genuinely critical, high-consequence take-off zone.',
    whatToWorkOn: [
      'Build this from advanced-level late/steep take-offs',
      'Commit fully -- hesitation is often the actual risk here',
      'Know the wave and your own limits before pushing further',
    ],
  },
  {
    id: 'ex-heavy-water-paddle-fitness',
    name: 'Heavy-Water Paddle Fitness',
    level: 'Expert',
    category: 'Paddling',
    definition: 'Paddle fitness and breath control sufficient for heavy, consequential conditions.',
    bestEnvironment: 'Dedicated fitness/breath-hold training, alongside heavy-water sessions.',
    whatToWorkOn: [
      'Build a structured swimming/paddling fitness routine',
      'Practice breath-hold training safely, ideally with guidance',
      'Never train or push limits alone in real surf',
    ],
  },
  {
    id: 'ex-advanced-ocean-reading',
    name: 'Advanced Ocean Reading',
    level: 'Expert',
    category: 'Ocean Knowledge',
    definition: 'Reading swell, tide, wind, and bathymetry together to predict how a spot will behave before it does.',
    bestEnvironment: 'Any spot, built up over many sessions of observation.',
    whatToWorkOn: [
      'Keep a mental (or written) record of conditions across sessions',
      'Notice patterns between forecast data and what actually happens',
      'Test your predictions and refine your read over time',
    ],
  },
  {
    id: 'ex-independent-risk-assessment',
    name: 'Independent High-Level Risk Assessment',
    level: 'Expert',
    category: 'Ocean Knowledge',
    definition: 'Making sound, independent go/no-go decisions in serious conditions, without relying on others.',
    bestEnvironment: 'Any high-consequence session -- this is a judgment skill, not wave-specific.',
    whatToWorkOn: [
      'Build a personal checklist of conditions/signs that mean no',
      'Practice walking away from a session that doesn’t check out',
      'Debrief honestly after close calls rather than dismissing them',
    ],
  },
  // Optional progressive manoeuvres -- NOT mandatory for Expert status.
  {
    id: 'ex-fin-release',
    name: 'Fin Release',
    level: 'Expert',
    category: 'Progressive Manoeuvres',
    definition: 'Deliberately releasing the fins from the water mid-turn for a loose, skating sensation.',
    bestEnvironment: 'A soft, forgiving wave section, ideally on a smaller/looser board.',
    whatToWorkOn: [
      'Carry speed into a turn, then ease rail pressure to break the fins free',
      'Keep your weight centred as the tail skates',
      'Reconnect the rail smoothly to exit',
    ],
    optional: true,
  },
  {
    id: 'ex-tail-slide',
    name: 'Tail Slide',
    level: 'Expert',
    category: 'Progressive Manoeuvres',
    definition: 'Intentionally sliding the tail of the board out during a turn.',
    bestEnvironment: 'A soft, playful wave section.',
    whatToWorkOn: [
      'Shift weight back and release rail pressure through a turn',
      'Use your arms and hips to control the slide',
      'Practice regaining control smoothly out of the slide',
    ],
    optional: true,
  },
  {
    id: 'ex-air-approach',
    name: 'Air Approach',
    level: 'Expert',
    category: 'Progressive Manoeuvres',
    definition: 'Building the speed and approach line needed to attempt leaving the water, without yet landing one.',
    bestEnvironment: 'A ramping, launchable section on a smaller wave.',
    whatToWorkOn: [
      'Build maximum controllable speed into the section',
      'Identify the right ramp/lip shape to launch from',
      'Focus on the approach and pop, before worrying about landing',
    ],
    optional: true,
  },
  {
    id: 'ex-first-air',
    name: 'First Air',
    level: 'Expert',
    category: 'Progressive Manoeuvres',
    definition: 'Leaving the water and landing back on the wave for the first time, in any form.',
    bestEnvironment: 'A forgiving, ramping section with a soft landing zone.',
    whatToWorkOn: [
      'Commit to the pop once your approach speed feels right',
      'Keep your board level and beneath you in the air',
      'Absorb the landing through bent knees and ride away',
    ],
    optional: true,
  },
  {
    id: 'ex-air-reverse',
    name: 'Air Reverse',
    level: 'Expert',
    category: 'Progressive Manoeuvres',
    definition: 'An aerial manoeuvre with a rotation, landing back in the direction you came from.',
    bestEnvironment: 'A ramping section, once basic airs are consistent.',
    whatToWorkOn: [
      'Build consistency with straight airs before adding rotation',
      'Initiate the rotation early, in the air, not on landing',
      'Spot your landing through the rotation, the same way you would in a turn',
    ],
    optional: true,
  },
];

export const ALL_ACHIEVEMENTS: Achievement[] = [
  ...absoluteBeginner,
  ...beginner,
  ...intermediate,
  ...advanced,
  ...expert,
];

export const ACHIEVEMENTS_BY_LEVEL: Record<SurfLevel, Achievement[]> = {
  'Absolute Beginner': absoluteBeginner,
  Beginner: beginner,
  Intermediate: intermediate,
  Advanced: advanced,
  Expert: expert,
};

/** Categories relevant to a level, in a sensible display order -- not every
 * category applies to every level (e.g. Barrel Skills only from Advanced on). */
export const categoriesForLevel = (level: SurfLevel): AchievementCategory[] => {
  const seen = new Set<AchievementCategory>();
  const ordered: AchievementCategory[] = [];
  for (const a of ACHIEVEMENTS_BY_LEVEL[level]) {
    if (!seen.has(a.category)) {
      seen.add(a.category);
      ordered.push(a.category);
    }
  }
  return ordered;
};

/** Mandatory (non-optional) achievement count for a level -- what "100% of
 * this level" actually means; optional progressive manoeuvres don't count
 * toward completion. */
export const mandatoryCountForLevel = (level: SurfLevel): number =>
  ACHIEVEMENTS_BY_LEVEL[level].filter((a) => !a.optional).length;
